import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, RendererFactory2, inject } from '@angular/core';
import {IOrdersDetails} from '@app/checkout/models/order-details.model';
import {Order} from '@app/checkout/models/order-request.model';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {FileUtils} from '@app/core/utils/file.utils';
import {EPdfDownloadStatus, IOrderResultsPdfState} from '@app/order/models/my-order-state.model';
import {IOrderStatus} from '@app/shared/models/orderStatus.model';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {MessageService} from 'primeng/api';
import {catchError, map, of, switchMap} from 'rxjs';
import {MyOrderService} from '../services/my-order.service';
import {
  MyOrderActions,
  downloadOrderResults,
  downloadOrderResultsFailure,
  downloadOrderResultsSuccess,
  getEditOrderDetails,
  getEditOrderDetailsFailure,
  getEditOrderDetailsSuccess,
  getOrderResultsPdfDetails,
  getOrderResultsPdfDetailsFailure,
  getOrderResultsPdfDetailsSuccess,
  loadListOfOrders,
  loadListOfOrdersFailure,
  loadListOfOrdersSuccess,
  loadOrderStatusFailure,
  loadOrderStatusSuccess,
} from './my-order.actions';

@Injectable()
export class MyOrderEffects {
  private readonly actions$ = inject(Actions);
  private readonly myOrderService = inject(MyOrderService);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly messageService = inject(MessageService);

  globalToast = 'global-toast';

  getOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyOrderActions.LoadListOfOrders),
      switchMap((action: ReturnType<typeof loadListOfOrders>) => this.loadListOfOrders(action))
    )
  );

  getOrderStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyOrderActions.LoadOrderStatus),
      switchMap(() => this.loadOrderStatus())
    )
  );

  getEditOrderInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyOrderActions.GetEditOrderDetails),
      switchMap((action: ReturnType<typeof getEditOrderDetails>) => this.getEditOrderInfo(action))
    )
  );

  getOrderResultsPdfInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyOrderActions.GetOrderResultsPdfDetails),
      switchMap((action: ReturnType<typeof getOrderResultsPdfDetails>) => this.getOrderResultsPdfInfo(action))
    )
  );

  downloadOrderResultsInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MyOrderActions.DownloadOrderResults),
      switchMap((action: ReturnType<typeof downloadOrderResults>) => this.downloadOrderResults(action))
    )
  );

  private loadListOfOrders(action: ReturnType<typeof loadListOfOrders>) {
    return this.myOrderService.getListOfOrders(action.request).pipe(
      map((response: IOrdersDetails) => loadListOfOrdersSuccess({orders: response})),
      catchError((error: HttpErrorResponse) => {
        return of(loadListOfOrdersFailure({reason: error.message}));
      })
    );
  }

  private loadOrderStatus() {
    return this.myOrderService.getListOfOrderStatus().pipe(
      map((response: IOrderStatus[]) => loadOrderStatusSuccess({orderStatus: response})),
      catchError((error: HttpErrorResponse) => {
        return of(loadOrderStatusFailure({reason: error.message}));
      })
    );
  }

  private getEditOrderInfo(action: ReturnType<typeof getEditOrderDetails>) {
    return this.myOrderService.getEditOrderDetails(action.orderId).pipe(
      switchMap((editOrderInfo: Order) => {
        return of(getEditOrderDetailsSuccess({editOrderInfo: editOrderInfo}));
      }),
      catchError((err: HttpErrorResponse) => {
        ErrorHandlerUtils.showErrorMessage(this.messageService, this.globalToast, err.error ?? err.message);
        return of(getEditOrderDetailsFailure());
      })
    );
  }

  private getOrderResultsPdfInfo(action: ReturnType<typeof getOrderResultsPdfDetails>) {
    return this.myOrderService.getOrderResultsPdfDetails(action.orderId).pipe(
      switchMap((editOrderInfo: IOrderResultsPdfState[]) => {
        return of(getOrderResultsPdfDetailsSuccess({resultsInfo: editOrderInfo}));
      }),
      catchError(() => of(getOrderResultsPdfDetailsFailure()))
    );
  }

  private downloadOrderResults(action: ReturnType<typeof downloadOrderResults>) {
    return this.myOrderService.downloadOrderResults(action.orderId, action.name).pipe(
      switchMap((response: ArrayBuffer) => {
        FileUtils.downloadFile({
          data: response,
          filename: action.name,
          renderer: this.rendererFactory.createRenderer(null, null),
          type: 'application/pdf',
        });
        return of(
          downloadOrderResultsSuccess({
            downloadStatus: EPdfDownloadStatus.Success,
          })
        );
      }),
      catchError(() => of(downloadOrderResultsFailure()))
    );
  }
}
